import { Component, type ErrorInfo, type ReactNode } from "react";
import * as Sentry from "@sentry/react";

interface RouteErrorBoundaryProps {
  /**
   * Children to render when no error has been caught. Typically the route
   * tree (`<Routes />`).
   */
  children: ReactNode;
  /**
   * Whenever this value changes the boundary resets — i.e. forgets the
   * captured error and re-renders the children. Pass the current
   * router location here so navigating to a different route always
   * gets a fresh attempt instead of showing the stale fallback forever.
   */
  resetKey: string;
}

interface RouteErrorBoundaryState {
  /** The captured render error, or null when the boundary is healthy. */
  error: Error | null;
}

/**
 * Route-scoped React error boundary used inside `App.tsx`.
 *
 * Why this exists
 * ───────────────
 * The app already has a top-level `<Sentry.ErrorBoundary>` in `main.tsx` as
 * a true last-resort fallback. The problem with relying on it alone is
 * scope: any render error anywhere in the React tree (a single page, a
 * floating button, a transient lazy-import failure) trips the top-level
 * boundary and blanks the ENTIRE app — header, bottom nav, and all other
 * routes — until the user manually reloads.
 *
 * On a long-running native shell that's a hard failure mode for users:
 * a one-time render bug on `/create-listing` would persist until the
 * Android process is killed.
 *
 * What this boundary does
 * ───────────────────────
 * - Catches render-time errors thrown by descendants (one route at a time).
 * - Reports the error to Sentry (when configured) with route context so it
 *   doesn't get swallowed silently.
 * - Renders a friendly Arabic recovery card with three escape routes:
 *     1) Reload the page (full reset of the React + Capacitor WebView state).
 *     2) Go back to the previous screen.
 *     3) Jump to the home page.
 * - Resets automatically when `resetKey` changes — so as soon as the user
 *   navigates to a different route, the boundary forgets the error and the
 *   rest of the app continues to work normally. The top-level boundary
 *   in `main.tsx` is intentionally left alone as a deeper safety net.
 *
 * Safety
 * ──────
 * - Pure UI: no auth, payment, wallet, stock, order, return, or category
 *   logic is touched.
 * - The fallback never renders any user-supplied content (no XSS surface).
 * - Sentry capture is opt-in (`VITE_SENTRY_DSN` env var); a missing DSN is
 *   a no-op.
 */
export class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  constructor(props: RouteErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    try {
      if (import.meta.env.VITE_SENTRY_DSN) {
        Sentry.withScope((scope) => {
          scope.setTag("route_error_boundary", true);
          scope.setExtra("componentStack", errorInfo.componentStack);
          scope.setExtra(
            "location",
            typeof window !== "undefined" ? window.location.pathname : "",
          );
          Sentry.captureException(error);
        });
      }
    } catch {
      // Sentry must never throw out of a boundary.
    }
    if (typeof console !== "undefined" && typeof console.error === "function") {
      console.error("[RouteErrorBoundary] caught render error:", error);
    }
  }

  componentDidUpdate(prevProps: RouteErrorBoundaryProps): void {
    // Reset whenever the route key changes so the user can navigate away
    // from a broken page and back into a working one without reloading.
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  private handleReload = () => {
    try {
      window.location.reload();
    } catch {
      // Fall back to navigation if reload is blocked.
      try {
        window.location.href = "/";
      } catch {
        // last-resort no-op
      }
    }
  };

  private handleBack = () => {
    try {
      if (window.history.length > 1) {
        window.history.back();
      } else {
        window.location.href = "/";
      }
    } catch {
      // ignore
    }
  };

  private handleHome = () => {
    try {
      window.location.href = "/";
    } catch {
      // ignore
    }
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        className="min-h-[100dvh] flex flex-col items-center justify-start sm:justify-center gap-6 p-4 sm:p-8 pb-32 sm:pb-8 overflow-y-auto"
        dir="rtl"
        data-testid="route-error-boundary"
      >
        <div className="max-w-md w-full bg-card border rounded-2xl shadow-sm p-5 sm:p-6 space-y-4">
          <h1 className="text-xl sm:text-2xl font-bold text-center">
            تعذّر فتح هذه الصفحة
          </h1>
          <p className="text-sm text-muted-foreground text-center leading-relaxed">
            حدث خطأ غير متوقع أثناء عرض هذه الصفحة. يمكنك إعادة المحاولة أو
            العودة إلى الرئيسية. باقي أقسام التطبيق تعمل بشكل طبيعي.
          </p>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={this.handleReload}
              className="w-full px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium"
              data-testid="button-route-error-reload"
            >
              إعادة تحميل الصفحة
            </button>
            <button
              type="button"
              onClick={this.handleBack}
              className="w-full px-4 py-2 rounded-md border bg-background text-foreground text-sm"
              data-testid="button-route-error-back"
            >
              العودة للصفحة السابقة
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              className="w-full px-4 py-2 rounded-md text-muted-foreground text-sm"
              data-testid="button-route-error-home"
            >
              الذهاب للرئيسية
            </button>
          </div>
        </div>
      </div>
    );
  }
}
